import { Container, Graphics, Text } from 'pixi.js';
import gsap from 'gsap';
import { tween, delay, cancelDelay, Tween } from '../core/TweenManager';
import { FIGHTER_ZOI_BY_STATION_TIER, SETTLEMENT_CONFIG, type EntityType, type SelectionInfo } from '@astra/shared';

export interface SettlementData {
  id: string;
  name: string;
  x: number;
  y: number;
  tier: number;
  coreHull: number;
  coreMaxHull: number;
  coreExposed: boolean;
}

// A drone settlement — an emergent, attackable rogue-drone nest. Rendered as an
// angular derelict cluster (deliberately NOT a clean NPC station) with a circular
// core-HP ring near the core and a faint zone-of-influence ring (the fighters' turf).
// The core is always damageable; its HP + regen is the wall. See docs/drone_settlement.md.
const BODY_COLOR = 0x9b5cff;        // muted violet — "rogue tech"
const BODY_RADIUS = 425;            // outer plating ring
const CORE_RADIUS = 95;             // dark core circle

// HP bar (rectangular, above the body — matches asteroid/ship style).
const HP_BAR_WIDTH = 380;
const HP_BAR_HEIGHT = 22;
const HP_BAR_OFFSET_Y = -(BODY_RADIUS + 46);

export class Settlement extends Container {
  public readonly entityType: EntityType = 'station';
  public readonly id: string;
  public get selectionRadius(): number { return 470; }
  public get zoiRadius(): number {
    const t = Math.max(1, Math.min(4, this.tier)) as 1 | 2 | 3 | 4;
    return FIGHTER_ZOI_BY_STATION_TIER[t] ?? 1500;
  }

  // Dockable like an NPC station — GameScene.checkStationZones treats stations, private
  // stations and settlements uniformly via these members (a settlement is a tradeable,
  // but unsafe, node). The server enforces the real dock range + the neutral-stance gate.
  public get stationId(): string { return this.id; }
  public get stationName(): string { return this.name; }
  public isInDockingRange(px: number, py: number): boolean {
    return Math.hypot(this.x - px, this.y - py) <= SETTLEMENT_CONFIG.DOCK_RADIUS;
  }

  public name: string;
  public coreHull: number;
  public coreMaxHull: number;
  public coreExposed: boolean;

  private tier: number;
  private body: Graphics;
  private halo: Graphics;
  private hpBar: Graphics;
  private zoiRing: Graphics;
  private nameTag: Text;
  private spin = 0;
  private hpBarHideDelay: gsap.core.Tween | null = null;
  private hpBarFadeTween: Tween | null = null;

  constructor(data: SettlementData) {
    super();
    this.id = data.id;
    this.name = data.name || 'Rogue Settlement';
    this.coreHull = data.coreHull;
    this.coreMaxHull = data.coreMaxHull;
    this.coreExposed = data.coreExposed;
    this.tier = data.tier || 1;
    this.position.set(data.x, data.y);

    // Zone of influence (the fighters' turf) — large, faint, drawn behind everything.
    this.zoiRing = new Graphics();
    this.addChild(this.zoiRing);
    this.drawZoiRing();

    // Static halo (cheap, no shader) — concentric faint circles that simulate a glow.
    // Replaces the previous GlowFilter, which forced a per-frame multi-pass blur over a
    // huge framebuffer whenever the body rotated. Drawn behind the body, never spins.
    this.halo = new Graphics();
    this.addChild(this.halo);
    this.drawHalo();

    this.body = new Graphics();
    this.drawBody();
    this.addChild(this.body);

    // Rectangular core-HP bar above the body — hidden by default, shown momentarily
    // when the settlement takes a hit (same dynamic as the PvP ship HP bar).
    this.hpBar = new Graphics();
    this.hpBar.visible = false;
    this.addChild(this.hpBar);
    this.drawHpBar();

    this.nameTag = new Text({
      text: '⚠ Rogue Settlement',
      style: { fontFamily: 'monospace', fontSize: 42, fill: 0xffaa66, align: 'center' },
    });
    this.nameTag.anchor.set(0.5, 1);
    this.nameTag.y = HP_BAR_OFFSET_Y - 14;
    this.addChild(this.nameTag);
  }

  private drawHalo(): void {
    const g = this.halo;
    g.clear();
    // Three concentric circles fading outward — drawn once, batched, zero shader cost.
    g.circle(0, 0, BODY_RADIUS + 60);
    g.fill({ color: BODY_COLOR, alpha: 0.05 });
    g.circle(0, 0, BODY_RADIUS + 30);
    g.fill({ color: BODY_COLOR, alpha: 0.08 });
    g.circle(0, 0, BODY_RADIUS + 12);
    g.fill({ color: BODY_COLOR, alpha: 0.12 });
  }

  private drawBody(): void {
    const g = this.body;
    g.clear();
    const color = BODY_COLOR;
    // Outer ring of scavenged plating
    g.circle(0, 0, BODY_RADIUS);
    g.stroke({ width: 10, color, alpha: 0.55 });
    // Angular hull cluster (irregular hexagon — looks cobbled together, not manufactured)
    const pts: number[] = [];
    const spikes = 7;
    for (let i = 0; i < spikes; i++) {
      const a = (i / spikes) * Math.PI * 2;
      const r = BODY_RADIUS * (i % 2 === 0 ? 0.62 : 0.42);
      pts.push(Math.cos(a) * r, Math.sin(a) * r);
    }
    g.poly(pts);
    g.fill({ color, alpha: 0.85 });
    g.stroke({ width: 6, color: 0xffffff, alpha: 0.4 });
    // Core
    g.circle(0, 0, CORE_RADIUS);
    g.fill({ color: 0x2a1a44, alpha: 0.95 });
    g.stroke({ width: 7, color, alpha: 0.85 });
  }

  /** Rectangular HP bar above the body, mirroring asteroid/ship style. Always visible. */
  private drawHpBar(): void {
    const g = this.hpBar;
    g.clear();
    if (this.coreMaxHull <= 0) return;
    const frac = Math.max(0, Math.min(1, this.coreHull / this.coreMaxHull));
    const x = -HP_BAR_WIDTH / 2;
    const y = HP_BAR_OFFSET_Y;
    // Background (dark, slightly larger for border effect).
    g.roundRect(x - 2, y - 2, HP_BAR_WIDTH + 4, HP_BAR_HEIGHT + 4, 4);
    g.fill({ color: 0x000000, alpha: 0.75 });
    // Fill — green > orange > red, matching DamageableEntity.
    if (frac > 0) {
      const fillColor = frac > 0.5 ? 0x44ff44 : frac > 0.25 ? 0xffaa00 : 0xff6644;
      g.roundRect(x, y, HP_BAR_WIDTH * frac, HP_BAR_HEIGHT, 3);
      g.fill({ color: fillColor, alpha: 1 });
    }
  }

  /** Zone-of-influence ring (radius = fighter ZoI for the tier) — where its fighters patrol. */
  private drawZoiRing(): void {
    const g = this.zoiRing;
    g.clear();
    const zoi = this.zoiRadius;
    // Outer soft wash — wide, low-alpha, gives the ring presence at zoomed-out distances.
    g.circle(0, 0, zoi);
    g.stroke({ width: 40, color: BODY_COLOR, alpha: 0.12 });
    // Crisp boundary line on top.
    g.circle(0, 0, zoi);
    g.stroke({ width: 6, color: BODY_COLOR, alpha: 0.5 });
  }

  updateFromServer(data: Partial<SettlementData>): void {
    if (data.x !== undefined) this.x = data.x;
    if (data.y !== undefined) this.y = data.y;
    if (data.coreHull !== undefined) this.coreHull = data.coreHull;
    if (data.coreMaxHull !== undefined) this.coreMaxHull = data.coreMaxHull;
    if (data.coreExposed !== undefined) this.coreExposed = data.coreExposed;
    this.drawHpBar();
  }

  /** Reveal the HP bar momentarily — called on each hit. Auto-fades after 2s of no hits. */
  showHpBar(): void {
    this.drawHpBar();
    this.hpBar.visible = true;
    this.hpBar.alpha = 1;
    if (this.hpBarFadeTween) { this.hpBarFadeTween.stop(); this.hpBarFadeTween = null; }
    if (this.hpBarHideDelay) cancelDelay(this.hpBarHideDelay);
    this.hpBarHideDelay = delay(2000, () => {
      this.hpBarFadeTween = tween({
        targets: this.hpBar,
        alpha: 0,
        duration: 300,
        onComplete: () => { this.hpBar.visible = false; this.hpBarFadeTween = null; },
      });
    });
  }

  update(delta: number): void {
    // Slow ominous rotation of the plating ring. Cheap now that the body has no filter —
    // PIXI re-batches the rotated Graphics directly without re-rendering an offscreen target.
    this.spin += delta * 0.0004;
    this.body.rotation = this.spin;
  }

  getSelectionInfo(): SelectionInfo {
    return {
      type: this.entityType,
      id: this.id,
      name: '⚠ Rogue Settlement',
      x: this.x,
      y: this.y,
      data: {
        isSettlement: true,
        coreHull: this.coreHull,
        coreMaxHull: this.coreMaxHull,
        coreExposed: this.coreExposed,
      },
    };
  }

  override destroy(options?: Parameters<Container['destroy']>[0]): void {
    if (this.hpBarFadeTween) { this.hpBarFadeTween.stop(); this.hpBarFadeTween = null; }
    if (this.hpBarHideDelay) { cancelDelay(this.hpBarHideDelay); this.hpBarHideDelay = null; }
    super.destroy(options);
  }

  explode(): void {
    if (!this.parent) return;
    const parent = this.parent;
    const cx = this.x, cy = this.y;
    this.visible = false;
    const flash = new Graphics();
    flash.circle(0, 0, BODY_RADIUS * 1.4);
    flash.fill({ color: 0xffd066, alpha: 0.9 });
    flash.position.set(cx, cy);
    flash.zIndex = 90;
    parent.addChild(flash);
    tween({ targets: flash, alpha: 0, duration: 600, onComplete: () => flash.destroy() });
    for (let i = 0; i < 26; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 60 + Math.random() * 160;
      const piece = new Graphics();
      piece.circle(0, 0, 2 + Math.random() * 3);
      piece.fill({ color: [0xff7744, 0xffaa44, 0x9b5cff][i % 3], alpha: 1 });
      piece.position.set(cx, cy);
      piece.zIndex = 89;
      parent.addChild(piece);
      tween({
        targets: piece,
        x: cx + Math.cos(angle) * speed,
        y: cy + Math.sin(angle) * speed,
        alpha: 0,
        duration: 700 + Math.random() * 500,
        onComplete: () => piece.destroy(),
      });
    }
  }
}
